import { ColumnType } from 'src/components/molecules';
import { ACTION_COLUMN_WIDTH } from './CategoryDetailsPage/categoryDetailsTableConfig';
import { FormData } from './types';

export type FormManagementColumnKey =
  | 'state'
  | 'title'
  | 'category'
  | 'country'
  | 'languages'
  | 'questionCount'
  | 'actions'
  | 'createdBy'
  | 'createdAt'
  | 'lastUpdated';

type ColumnMeta = {
  label: string;
  value: string;
  sortable: boolean;
  showOverflow?: boolean;
};

const COLUMN_META: Record<FormManagementColumnKey, ColumnMeta> = {
  state: { label: 'Status', value: 'state', sortable: true },
  title: { label: 'Form title', value: 'title', sortable: true },
  category: { label: 'Category', value: 'category', sortable: true },
  country: { label: 'Countries', value: 'country', sortable: true },
  languages: { label: 'Languages', value: 'languages', sortable: true },
  questionCount: {
    label: 'Questions',
    value: 'questions',
    sortable: true,
  },
  actions: {
    label: 'Actions',
    value: 'actions',
    sortable: false,
    showOverflow: true,
  },
  createdBy: { label: 'Created by', value: 'createdBy', sortable: true },
  createdAt: { label: 'Created at', value: 'createdAt', sortable: true },
  lastUpdated: { label: 'Last updated', value: 'lastUpdated', sortable: true },
};

const SPEAK_UP_COLUMN_KEYS: FormManagementColumnKey[] = [
  'state',
  'title',
  'category',
  'languages',
  'questionCount',
  'createdBy',
  'createdAt',
  'lastUpdated',
  'actions',
];

const COI_COLUMN_KEYS: FormManagementColumnKey[] = [
  'state',
  'title',
  'category',
  'country',
  'languages',
  'questionCount',
  'createdBy',
  'createdAt',
  'lastUpdated',
  'actions',
];

export function resolveFormManagementColumnKeys(
  _speakUpEnabled: boolean,
  _coiEnabled: boolean,
  isCoiTab?: boolean,
): FormManagementColumnKey[] {
  return isCoiTab ? [...COI_COLUMN_KEYS] : [...SPEAK_UP_COLUMN_KEYS];
}

export type FormManagementTableConfig = {
  columnKeys: FormManagementColumnKey[];
  columns: ColumnType[];
  sortableColumnLabels: string[];
};

export function resolveFormManagementTableConfig(
  speakUpEnabled: boolean,
  coiEnabled: boolean,
  isCoiTab?: boolean,
): FormManagementTableConfig {
  const columnKeys = resolveFormManagementColumnKeys(
    speakUpEnabled,
    coiEnabled,
    isCoiTab,
  );
  const columns: ColumnType[] = columnKeys.map((key) => {
    const meta = COLUMN_META[key];
    const col: ColumnType = {
      label: meta.label,
      value: meta.value,
    };
    if (meta.showOverflow === true) {
      col.showOverflow = true;
    }
    return col;
  });
  const sortableColumnLabels = columnKeys
    .filter((key) => COLUMN_META[key].sortable)
    .map((key) => COLUMN_META[key].label);

  return { columnKeys, columns, sortableColumnLabels };
}

const FIGMA_HEADER_WIDTH_PX: Record<
  Exclude<FormManagementColumnKey, 'actions'>,
  string
> = {
  state: '150px',
  title: '250px',
  category: '160px',
  country: '160px',
  languages: '160px',
  questionCount: '160px',
  createdBy: '160px',
  createdAt: '160px',
  lastUpdated: '160px',
};

export function getFormManagementColumnWidths(
  columnKeys: FormManagementColumnKey[],
): (string | undefined)[] {
  return columnKeys.map((key) => {
    if (key === 'actions') return ACTION_COLUMN_WIDTH;
    return FIGMA_HEADER_WIDTH_PX[key];
  });
}

export function getFormManagementScalarCell(
  form: FormData,
  key: FormManagementColumnKey,
  formatYmd: (iso: string) => string,
): string | number {
  switch (key) {
    case 'state':
      return form.status;
    case 'title':
      return form.title;
    case 'category':
      return form.category;
    case 'country':
      return String(form.countryCount);
    case 'languages':
      return form.languages ?? '';
    case 'questionCount':
      return form.questionsCount;
    case 'createdBy':
      return form.createdBy;
    case 'createdAt':
      return formatYmd(form.createdAt);
    case 'lastUpdated':
      return formatYmd(form.lastUpdated);
    case 'actions':
      return '';
  }
}
